import { RangeType, Type } from 'shared/utils/flowstep';


//
// Headers
//
export const RESULT_TABLE_COLUMN_OFFSET = 1;
export const PUSH_FLOWSTEP_RESULT_HEADER = {
  Result: { key: 'result', header: 'Operation results' },
  Id: { key: 'id', header: 'Ids' },
  Date: { key: 'date', header: 'Date time' },
  LeadId: { key: 'leadId', header: 'Lead Id' },
  AccountId: { key: 'accountId', header: 'Account Id' },
  OpportunityId: { key: 'opportunityId', header: 'Opportunity Id' },
  ContactId: { key: 'contactId', header: 'Contact Id' },
  RelatedPersonAccountId: { key: 'relatedPersonAccountId', header: 'Related Person Account Id' },
  MergeId: { key: 'id', header: 'Master Id' },
  MergedIds: { key: 'mergedRecordIds', header: 'Merged Records Ids' },
  UpdatedRelatedIds: { key: 'updatedRelatedIds', header: 'Updated Related Ids' },
};

export const PULL_FLOWSTEP_ADDITIONAL_HEADERS = {
  AppendDateTime: 'Import Date/Time',
}

export const PUSH_FLOWSTEP_RESULT_HEADERS = [
  PUSH_FLOWSTEP_RESULT_HEADER.Result,
  PUSH_FLOWSTEP_RESULT_HEADER.Id,
  PUSH_FLOWSTEP_RESULT_HEADER.Date,
];

export const CONVERT_LEADS_RESULT_HEADERS = [
  PUSH_FLOWSTEP_RESULT_HEADER.Result,
  PUSH_FLOWSTEP_RESULT_HEADER.LeadId,
  PUSH_FLOWSTEP_RESULT_HEADER.Date,
  PUSH_FLOWSTEP_RESULT_HEADER.AccountId,
  PUSH_FLOWSTEP_RESULT_HEADER.OpportunityId,
  PUSH_FLOWSTEP_RESULT_HEADER.ContactId,
  PUSH_FLOWSTEP_RESULT_HEADER.RelatedPersonAccountId,
];

export const MERGE_OBJECTS_RESULT_HEADERS = [
  PUSH_FLOWSTEP_RESULT_HEADER.Result,
  PUSH_FLOWSTEP_RESULT_HEADER.MergeId,
  PUSH_FLOWSTEP_RESULT_HEADER.Date,
  PUSH_FLOWSTEP_RESULT_HEADER.MergedIds,
  PUSH_FLOWSTEP_RESULT_HEADER.UpdatedRelatedIds,
];

export function getResultHeaders(type) {
  return type === Type.ConvertLeads
    ? CONVERT_LEADS_RESULT_HEADERS
    : type === Type.MergeObjects ? MERGE_OBJECTS_RESULT_HEADERS : PUSH_FLOWSTEP_RESULT_HEADERS;
}

export function getMaxBatchSize(type) {
  return type === Type.ConvertLeads ? 100 : 200;
}

export const MAX_PARALLEL_COUNT = 200;
export const DEFAULT_PARALLEL_COUNT = 25;


//
// Range type helpers for push flow steps
//
export const getNewRangeOptions = (rangeToApply) => {
  const nonEmptyColumns = rangeToApply.nonEmptyColumns;
  const filterFunction = (value) => rangeToApply.type === RangeType.Existing ? !!value : !value;
  const index = nonEmptyColumns[0];
  const isResultTable = index < 0;
  return { filterFunction, isResultTable, columnIndex: isResultTable ? convertResultRangeColumnIndex(index) : index };
}

export const getResultTableDelta = (dataTableColumnCount, headers = PUSH_FLOWSTEP_RESULT_HEADERS) => {
  return {
    deltas: {
      columnOffset: dataTableColumnCount + RESULT_TABLE_COLUMN_OFFSET,
      columnDelta: headers.length,
    },
    options: { useAbsoluteDelta: true }
  };
}

export const convertResultRangeColumnIndex = (index) => -index - 1;


//
// Batching
//
export const Limits = {
  // Sizes are in bytes
  OfficeWeb: { size: 2 * 1024 * 1024, contentFactor: 2.2, safetyFactor: 0.8 },
  Office365: { size: 512 * 1024, contentFactor: 2.2, safetyFactor: 0.85 },
  Mac: { size: 10 * 1024 * 1024, contentFactor: 2.2, safetyFactor: 0.85 },
  iOs: { size: 2 * 1024 * 1024, contentFactor: 2.2, safetyFactor: 0.85 },
  LambdaApiAsync: { size: 256 * 1024, contentFactor: 2.2, safetyFactor: 0.9 },
  LambdaApiSync: { size: 6 * 1024 * 1024, contentFactor: 2.2, safetyFactor: 0.9 },
};

export const DEFAULT_CELL_SYMBOLS_COUNT = 15;
export const DEFAULT_RESULTS_ROW_SYMBOLS_COUNT = 200;

export const MappingSymbolsCount = {
  id: 18,
  string: DEFAULT_CELL_SYMBOLS_COUNT,
  date: 5,
  dateTime: 10,
};

export function symbolsCountToBatchRowsCount(symbolsCount, limit) {
  return Math.floor(limit.size / ((symbolsCount || 1) * limit.contentFactor) * limit.safetyFactor);
}

export function fieldTypesToSymbolsCount(fieldTypes) {
  return fieldTypes.reduce((out, x) => out += (MappingSymbolsCount[x] ?? DEFAULT_CELL_SYMBOLS_COUNT), 0);
}

export function mappingToFieldTypes(mapping) {
  return mapping.map(x => x.columnData.rawField.name);
}

export function symbolsCountToBatches(symbolsCount, rowsCount, limit) {
  const rowsInBatch = symbolsCountToBatchRowsCount(symbolsCount, limit);
  return splitRowsToBatches(rowsCount, rowsInBatch);
}

export function splitRowsToBatches(rowsCount, rowsInBatch) {
  const batchesCount = Math.floor(rowsCount / rowsInBatch);
  const lastBatch = rowsCount % rowsInBatch;
  const batches = Array(batchesCount).fill(rowsInBatch);
  lastBatch && batches.push(lastBatch);
  return batches;
}

export async function executeInBatchesWithSpansTree(symbolsCountInRow, limits, spansTree, loader, mapper, syncer) {
  const tree = spansTree.clone();
  const rowsCount = tree.getValuesCount();
  return await executeInBatches(symbolsCountInRow, limits, rowsCount,
    async (chunkSize) => await loader(tree.pickCount(chunkSize)),
    mapper,
    syncer);
}

export async function executeInBatches(symbolsCountInRow, limits, rowsCount, loader, mapper, syncer) {
  const batches = symbolsCountToBatches(symbolsCountInRow, rowsCount, limits);
  let results = [];

  for (const chunkSize of batches) {
    const value = await loader(chunkSize);
    syncer && await syncer();
    const res = mapper ? await mapper(value) : value;
    if (res) {
      results = results.concat(res);
    }
  }

  return results;
}
